/**
 * /signup — INVITE-ONLY GATE.
 *
 * Self-serve signup is intentionally closed until the marketing site at
 * `3mote-web-new` ships the pricing-page-driven onboarding flow that
 * collects tenant info (company, team size, plan, payment method, etc.).
 *
 * Why the gate exists: a Cognito user created via the public sign-up
 * flow has no `users_tenants` row, which means `/api/me` 403s with no
 * recovery UX. Phase 1 parity audit caught this. Until the multi-step
 * pricing onboarding ships (Phase 4+), self-serve must be closed.
 *
 * For now, every new user comes in via the worker-invite flow (admin
 * invites → Cognito `AdminCreateUser` + `users_tenants` insertion in
 * the same tx). Existing users can still sign in at `/login`.
 *
 * The Cognito User Pool itself should also be set to
 * `AdminCreateUserOnly: true` as defense in depth — track that as an
 * `3mote-infra-new` follow-up so direct API hits to the SignUp endpoint
 * are also rejected.
 */

import { Link, createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/signup')({
  component: SignupGatePage,
})

function SignupGatePage() {
  const { t } = useTranslation('auth')
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-lg border border-border bg-bg-elevated p-8 shadow-sm">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-bg-muted px-3 py-1 text-xs font-medium text-fg-muted">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          {t('signup.badge')}
        </div>
        <h1 className="mb-3 text-2xl font-semibold text-fg">{t('signup.title')}</h1>
        <p className="mb-2 text-sm text-fg-muted">{t('signup.bodyParagraph1')}</p>
        <p className="mb-6 text-sm text-fg-muted">
          {t('signup.bodyParagraph2Prefix')}
          <a
            className="font-medium text-fg underline decoration-fg-subtle underline-offset-2 hover:decoration-fg"
            href="mailto:hello@aeeiee.com?subject=3mote%20early%20access"
          >
            hello@aeeiee.com
          </a>
          {t('signup.bodyParagraph2Suffix')}
        </p>
        <div className="rounded-md bg-bg-muted px-4 py-3 text-sm text-fg-muted">
          <strong className="font-medium text-fg">{t('signup.alreadyHaveInvite')}</strong>{' '}
          <Link
            className="text-fg underline decoration-fg-subtle underline-offset-2 hover:decoration-fg"
            to="/login"
          >
            {t('signup.signInLink')}
          </Link>
        </div>
      </div>
    </div>
  )
}
